import { apiService } from './apiService';
import type { 
  ComplaintDetailResponse, 
  ComplaintPageResponse, 
  ComplaintListItem,
  ComplaintStatus,
  UpdateComplaintRequest 
} from '../../generated-src/client/complaints/api';

// Маппинг статусов API на внутренние статусы
const mapApiStatusToInternal = (apiStatus: ComplaintStatus): 'new' | 'in_progress' | 'closed' => {
  switch (apiStatus) {
    case 'CREATED':
    case 'SENT':
    case 'ERROR':
      return 'new';
    case 'FIXED':
    case 'SKIPPED':
      return 'closed';
    default:
      return 'new';
  }
};

const mapInternalStatusToApi = (internalStatus: 'new' | 'in_progress' | 'closed'): ComplaintStatus => {
  switch (internalStatus) {
    case 'new':
      return 'CREATED';
    case 'in_progress':
      return 'SENT';
    case 'closed':
      return 'FIXED';
    default:
      return 'CREATED';
  }
};

/**
 * Интерфейс жалобы пользователя
 */
export interface Complaint {
  /** Уникальный идентификатор жалобы */
  id: number;
  /** Имя автора жалобы */
  author: string;
  /** Email автора жалобы */
  authorEmail: string;
  /** Тип жалобы */
  type: string;
  /** Статус жалобы */
  status: 'new' | 'in_progress' | 'closed';
  /** Описание жалобы */
  description: string;
  /** Дата создания */
  createdAt: string;
  /** Дата последнего обновления */
  updatedAt: string;
  /** Ответ на жалобу (опционально) */
  reply?: string;
  /** Дата ответа (опционально) */
  repliedAt?: string;
  /** Автор ответа (опционально) */
  repliedBy?: string;
}

/**
 * Интерфейс ответа на жалобу
 */
export interface ComplaintReply {
  /** ID жалобы для ответа */
  complaintId: number;
  /** Текст ответа */
  reply: string;
  /** Новый статус жалобы (опционально) */
  status?: 'in_progress' | 'closed';
}

/**
 * Интерфейс фильтров для поиска жалоб
 */
export interface ComplaintsFilters {
  /** Фильтр по статусу жалобы */
  status?: string;
  /** Фильтр по типу жалобы */
  type?: string;
  /** Поисковый запрос */
  search?: string;
  /** Номер страницы */
  page?: number;
  /** Размер страницы */
  size?: number;
}

/**
 * Интерфейс ответа с пагинированным списком жалоб
 */
export interface ComplaintsResponse {
  /** Список жалоб */
  items: Complaint[];
  /** Общее количество жалоб */
  total: number;
  /** Номер текущей страницы */
  page: number;
  /** Размер страницы */
  size: number;
  /** Общее количество страниц */
  totalPages: number;
}

/**
 * Сервис для работы с жалобами
 * Интегрирует данные из API в компоненты управления жалобами
 */
/**
 * Сервис для работы с жалобами пользователей
 * Предоставляет методы для получения, создания и обновления жалоб
 * 
 * @author HR Recruiter Team
 * @version 1.0.0
 * @since 2024-01-15
 */
class ComplaintsService {
  /**
   * Получить список жалоб с фильтрацией и пагинацией
   * @param filters - Фильтры для поиска жалоб
   * @param page - Номер страницы (начиная с 0)
   * @param size - Размер страницы
   * @returns Promise<ComplaintsResponse> - Пагинированный список жалоб
   */
  async getComplaints(filters: ComplaintsFilters = {}): Promise<ComplaintsResponse> {
    try {
      const client = await apiService.getAuthenticatedClient();
      
      // Параметры запроса
      const params: any = {
        page: filters.page || 0,
        size: filters.size || 10
      };
      
      if (filters.status && filters.status !== 'all') {
        params.status = mapInternalStatusToApi(filters.status as 'new' | 'in_progress' | 'closed');
      }

      console.log('📋 Загружаем жалобы с параметрами:', params);
      
      // Реальный API вызов
      const response = await client.complaints.getComplaints(
        params.page,
        params.size,
        params.status,
        params.senderEmail,
        'createdAt,DESC'
      );
      
      const data: ComplaintPageResponse = response.data;

      // Маппинг ответа API на внутренний формат
      const complaints: Complaint[] = await Promise.all(
        data.content.map(async (item: ComplaintListItem) => {
          try {
            // Загружаем детали жалобы для получения текста
            const detailResponse = await client.complaints.getComplaintById(item.id);
            const detailData: ComplaintDetailResponse = detailResponse.data;
            
            return {
              id: item.id,
              author: item.senderEmail.split('@')[0], // Извлекаем имя из email
              authorEmail: item.senderEmail,
              type: 'Жалоба', // По умолчанию, так как API не возвращает тип
              status: mapApiStatusToInternal(item.status),
              description: detailData.text,
              createdAt: item.createdAt,
              updatedAt: item.createdAt,
              reply: detailData.developerComment || undefined,
              repliedAt: detailData.closedAt || undefined,
              repliedBy: detailData.developerComment ? 'admin@company.com' : undefined
            };
          } catch (error) {
            console.error(`Ошибка загрузки деталей жалобы ${item.id}:`, error);
            return {
              id: item.id,
              author: item.senderEmail.split('@')[0],
              authorEmail: item.senderEmail,
              type: 'Жалоба',
              status: mapApiStatusToInternal(item.status),
              description: 'Ошибка загрузки описания',
              createdAt: item.createdAt,
              updatedAt: item.createdAt,
              reply: undefined,
              repliedAt: undefined,
              repliedBy: undefined
            };
          }
        })
      );

      return {
        items: complaints,
        total: data.totalElements,
        page: data.page,
        size: data.size,
        totalPages: data.totalPages
      };

    } catch (error) {
      console.error('Ошибка загрузки жалоб:', error);
      throw new Error('Не удалось загрузить список жалоб');
    }
  }

  /**
   * Получить детали жалобы по ID
   */
  async getComplaintById(id: number): Promise<Complaint> {
    try {
      const client = await apiService.getAuthenticatedClient();
      
      console.log('📋 Загружаем жалобу по ID:', id);
      
      // Реальный API вызов
      const response = await client.complaints.getComplaintById(id);
      const data: ComplaintDetailResponse = response.data;

      // Маппинг ответа API на внутренний формат
      const complaint: Complaint = {
        id: data.id,
        author: data.senderEmail.split('@')[0], // Извлекаем имя из email
        authorEmail: data.senderEmail,
        type: 'Жалоба', // По умолчанию, так как API не возвращает тип
        status: mapApiStatusToInternal(data.status),
        description: data.text,
        createdAt: data.createdAt,
        updatedAt: data.createdAt,
        reply: data.developerComment || undefined,
        repliedAt: data.closedAt || undefined,
        repliedBy: data.developerComment ? 'admin@company.com' : undefined
      };

      return complaint;

    } catch (error) {
      console.error('Ошибка загрузки жалобы:', error);
      throw new Error('Не удалось загрузить жалобу');
    }
  }

  /**
   * Ответить на жалобу
   * @param replyData - Данные ответа на жалобу
   * @param replyData.complaintId - ID жалобы
   * @param replyData.reply - Текст ответа
   * @param replyData.status - Новый статус жалобы (опционально)
   */
  async replyToComplaint(replyData: ComplaintReply): Promise<void> {
    try {
      const client = await apiService.getAuthenticatedClient();
      
      console.log('💬 Отправляем ответ на жалобу:', replyData);
      
      // Подготавливаем данные для API
      const updateRequest: UpdateComplaintRequest = {
        developerComment: replyData.reply
      };

      // Если указан статус, добавляем его
      if (replyData.status) {
        updateRequest.status = mapInternalStatusToApi(replyData.status);
      }

      // Реальный API вызов
      await client.complaints.updateComplaint(replyData.complaintId, updateRequest);
      
      console.log('✅ Ответ на жалобу успешно сохранен');

    } catch (error) {
      console.error('Ошибка отправки ответа:', error);
      throw new Error('Не удалось отправить ответ на жалобу');
    }
  }

  /**
   * Обновить статус жалобы
   * @param id - ID жалобы
   * @param status - Новый статус жалобы
   */
  async updateComplaintStatus(id: number, status: 'new' | 'in_progress' | 'closed'): Promise<void> {
    try {
      const client = await apiService.getAuthenticatedClient();
      
      console.log('🔄 Обновляем статус жалобы:', { id, status });
      
      // Подготавливаем данные для API
      const updateRequest: UpdateComplaintRequest = {
        status: mapInternalStatusToApi(status)
      };

      // Реальный API вызов
      await client.complaints.updateComplaint(id, updateRequest);
      
      console.log('✅ Статус жалобы успешно обновлен');

    } catch (error) {
      console.error('Ошибка обновления статуса:', error);
      throw new Error('Не удалось обновить статус жалобы');
    }
  }
}

export const complaintsService = new ComplaintsService();
